import { isEmbeddedInLobby } from '@/logic/embedded-mode';
import { resolveWebWrapper } from '@/logic/web-wrapper/web-wrapper-bridge';
import { setUserSessionContext } from '@/store/actions';
import { setSessionStartTimeCookie } from '@/utilities/setters/cookieSetters';

export type EmbeddedWebWrapperSession = {
  jwt: string;
  loginToken?: string;
  sessionId: string;
  userId?: number | string;
  created?: string;
};

const isEmbeddedWebWrapperSession = (
  value: unknown,
): value is EmbeddedWebWrapperSession => {
  if (!value || typeof value !== 'object') return false;

  const session = value as Partial<EmbeddedWebWrapperSession>;
  return (
    typeof session.jwt === 'string' &&
    session.jwt.length > 0 &&
    typeof session.sessionId === 'string' &&
    session.sessionId.length > 0 &&
    (session.loginToken === undefined ||
      typeof session.loginToken === 'string') &&
    (session.created === undefined ||
      (typeof session.created === 'string' &&
        !Number.isNaN(Date.parse(session.created)))) &&
    (session.userId === undefined ||
      (typeof session.userId === 'string' && session.userId.length > 0) ||
      (typeof session.userId === 'number' && Number.isFinite(session.userId)))
  );
};

const resolveUserId = (
  session: EmbeddedWebWrapperSession,
  wrapperUserId: unknown,
): string | null => {
  if (
    session.userId !== undefined &&
    wrapperUserId !== null &&
    wrapperUserId !== undefined &&
    String(session.userId) !== String(wrapperUserId)
  ) {
    return null;
  }

  const userId = wrapperUserId ?? session.userId;
  if (
    (typeof userId !== 'string' && typeof userId !== 'number') ||
    String(userId).length === 0
  ) {
    return null;
  }

  return String(userId);
};

export const hydrateEmbeddedSession = async (): Promise<boolean> => {
  if (!isEmbeddedInLobby()) return false;

  const wrapper = resolveWebWrapper();
  if (!wrapper) return false;

  const [sessionValue, wrapperUserId] = await Promise.all([
    wrapper.getSession(),
    wrapper.getUserId(),
  ]);

  if (!isEmbeddedWebWrapperSession(sessionValue)) return false;

  const userId = resolveUserId(sessionValue, wrapperUserId);
  if (!userId) return false;

  const created = sessionValue.created ?? '';

  if (created) {
    setSessionStartTimeCookie(created);
  }

  setUserSessionContext({
    authToken: { jwt: sessionValue.jwt, created: new Date(created) },
    ...(sessionValue.loginToken
      ? { loginToken: { jwt: sessionValue.loginToken } }
      : {}),
    sessionId: sessionValue.sessionId,
    userId,
    ...(created ? { sessionStartTime: new Date(created) } : {}),
    hasActiveSession: true,
  });

  return true;
};

export const requestEmbeddedSessionKeepAlive = async (): Promise<boolean> => {
  if (!isEmbeddedInLobby()) return false;

  const wrapper = resolveWebWrapper();
  if (!wrapper) return false;

  await wrapper.requestKeepAlive();
  return true;
};
